import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Tables } from '@/integrations/supabase/types';

export type Match = Tables<'matches'>;

export type MatchWithProfiles = Match & {
  player1_username?: string;
  player2_username?: string;
  player1_avatar?: string;
  player2_avatar?: string;
  event_name?: string;
};

async function attachProfiles(matches: Match[]): Promise<MatchWithProfiles[]> {
  if (!matches.length) return [];
  const userIds = [
    ...new Set(
      matches.flatMap(m => [m.player1_id, m.player2_id]).filter(Boolean) as string[],
    ),
  ];
  const eventIds = [...new Set(matches.map(m => m.event_id).filter(Boolean) as string[])];
  const [{ data: profiles }, { data: events }] = await Promise.all([
    userIds.length
      ? supabase.from('profiles').select('user_id, username, avatar_emoji').in('user_id', userIds)
      : Promise.resolve({ data: [] as any[] }),
    eventIds.length
      ? supabase.from('events').select('id, name').in('id', eventIds)
      : Promise.resolve({ data: [] as any[] }),
  ]);
  const pMap = new Map((profiles || []).map(p => [p.user_id, p]));
  const eMap = new Map((events || []).map(e => [e.id, e]));
  return matches.map(m => ({
    ...m,
    player1_username: m.player1_id ? pMap.get(m.player1_id)?.username : m.player1_label || undefined,
    player2_username: m.player2_id ? pMap.get(m.player2_id)?.username : m.player2_label || undefined,
    player1_avatar: m.player1_id ? pMap.get(m.player1_id)?.avatar_emoji : undefined,
    player2_avatar: m.player2_id ? pMap.get(m.player2_id)?.avatar_emoji : undefined,
    event_name: m.event_id ? eMap.get(m.event_id)?.name : undefined,
  }));
}

/** All matches I played in or created (across casual/event play). */
export function useMyMatches() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['my-matches', user?.id],
    enabled: !!user,
    queryFn: async () => {
      if (!user) return [] as MatchWithProfiles[];
      const { data, error } = await supabase
        .from('matches')
        .select('*')
        .or(`player1_id.eq.${user.id},player2_id.eq.${user.id},created_by.eq.${user.id}`)
        .order('played_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return attachProfiles((data || []) as Match[]);
    },
  });
}

export function useEventMatches(eventId: string | null) {
  return useQuery({
    queryKey: ['event-matches', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      if (!eventId) return [] as MatchWithProfiles[];
      const { data, error } = await supabase
        .from('matches')
        .select('*')
        .eq('event_id', eventId)
        .order('round', { ascending: true })
        .order('bracket_index', { ascending: true });
      if (error) throw error;
      return attachProfiles((data || []) as Match[]);
    },
  });
}

export function useLogMatch() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      eventId?: string | null;
      opponentId?: string | null;
      opponentLabel?: string;
      myDeckId?: string | null;
      opponentDeckId?: string | null;
      result: 'win' | 'loss' | 'draw';
      myScore?: number;
      opponentScore?: number;
      notes?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');
      const winnerId =
        input.result === 'draw'
          ? null
          : input.result === 'win'
          ? user.id
          : input.opponentId ?? null;
      const { error } = await supabase.from('matches').insert({
        event_id: input.eventId ?? null,
        created_by: user.id,
        player1_id: user.id,
        player2_id: input.opponentId ?? null,
        player2_label: input.opponentId ? null : input.opponentLabel ?? 'Opponent',
        player1_deck_id: input.myDeckId ?? null,
        player2_deck_id: input.opponentDeckId ?? null,
        winner_id: winnerId,
        player1_score: input.myScore ?? null,
        player2_score: input.opponentScore ?? null,
        notes: input.notes ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-matches', user?.id] });
      qc.invalidateQueries({ queryKey: ['event-matches'] });
    },
  });
}

export function useUpdateMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      winner_id?: string | null;
      player1_score?: number | null;
      player2_score?: number | null;
      notes?: string | null;
    }) => {
      const { id, ...rest } = input;
      const { error } = await supabase.from('matches').update(rest).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event-matches'] });
      qc.invalidateQueries({ queryKey: ['my-matches'] });
    },
  });
}

export function useDeleteMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('matches').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-matches'] });
      qc.invalidateQueries({ queryKey: ['event-matches'] });
    },
  });
}

export type DeckMeta = { id: string; name: string; ink_1: string; ink_2: string };
export type DeckBreakdownRow = {
  key: string;
  label: string;
  deckId: string | null;
  ink_1?: string;
  ink_2?: string;
  wins: number;
  losses: number;
  draws: number;
  total: number;
  winRate: number;
};

/** Group the user's matches by the deck they played and compute per-deck W/L. */
export function computeDeckBreakdown(
  matches: MatchWithProfiles[] | undefined,
  userId: string | undefined,
  decks: DeckMeta[] | undefined,
): DeckBreakdownRow[] {
  if (!matches || !userId) return [];
  const deckMap = new Map((decks || []).map(d => [d.id, d]));
  const buckets = new Map<string, DeckBreakdownRow>();
  for (const m of matches) {
    const isP1 = m.player1_id === userId;
    const isP2 = m.player2_id === userId;
    if (!isP1 && !isP2) continue;
    const myDeckId = (isP1 ? m.player1_deck_id : m.player2_deck_id) ?? null;
    const key = myDeckId ?? '__unassigned__';
    const meta = myDeckId ? deckMap.get(myDeckId) : undefined;
    const row = buckets.get(key) || {
      key,
      label: meta?.name || (myDeckId ? 'Deleted deck' : 'No deck logged'),
      deckId: myDeckId,
      ink_1: meta?.ink_1,
      ink_2: meta?.ink_2,
      wins: 0, losses: 0, draws: 0, total: 0, winRate: 0,
    };
    const draw = m.winner_id === null && m.player1_score != null && m.player1_score === m.player2_score;
    if (draw) row.draws += 1;
    else if (m.winner_id === userId) row.wins += 1;
    else if (m.winner_id) row.losses += 1;
    else continue;
    row.total = row.wins + row.losses + row.draws;
    row.winRate = row.total ? Math.round((row.wins / row.total) * 100) : 0;
    buckets.set(key, row);
  }
  return [...buckets.values()].sort((a, b) => b.total - a.total);
}

/** Group by ink-pair archetype (e.g. Amber/Steel) across all decks. */
export function computeArchetypeBreakdown(
  rows: DeckBreakdownRow[],
): DeckBreakdownRow[] {
  const buckets = new Map<string, DeckBreakdownRow>();
  for (const r of rows) {
    if (!r.ink_1 || !r.ink_2) continue;
    const pair = [r.ink_1, r.ink_2].sort();
    const key = pair.join('/');
    const existing = buckets.get(key) || {
      key,
      label: `${cap(pair[0])} / ${cap(pair[1])}`,
      deckId: null,
      ink_1: pair[0],
      ink_2: pair[1],
      wins: 0, losses: 0, draws: 0, total: 0, winRate: 0,
    };
    existing.wins += r.wins;
    existing.losses += r.losses;
    existing.draws += r.draws;
    existing.total = existing.wins + existing.losses + existing.draws;
    existing.winRate = existing.total ? Math.round((existing.wins / existing.total) * 100) : 0;
    buckets.set(key, existing);
  }
  return [...buckets.values()].sort((a, b) => b.total - a.total);
}

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }

/** Most recently used deck for the current user, for auto-fill in match logging. */
export function lastUsedDeckId(matches: MatchWithProfiles[] | undefined, userId: string | undefined): string | null {
  if (!matches || !userId) return null;
  for (const m of matches) {
    if (m.player1_id === userId && m.player1_deck_id) return m.player1_deck_id;
    if (m.player2_id === userId && m.player2_deck_id) return m.player2_deck_id;
  }
  return null;
}

/** Compute W/L stats for the current user from a match list. */
export function computeRecord(matches: MatchWithProfiles[] | undefined, userId: string | undefined) {
  if (!matches || !userId) return { wins: 0, losses: 0, draws: 0, total: 0, winRate: 0 };
  let wins = 0, losses = 0, draws = 0;
  for (const m of matches) {
    const involved = m.player1_id === userId || m.player2_id === userId || m.created_by === userId;
    if (!involved) continue;
    if (m.winner_id === null && (m.player1_score != null || m.player2_score != null) && m.player1_score === m.player2_score) {
      draws += 1;
    } else if (m.winner_id === userId) {
      wins += 1;
    } else if (m.winner_id) {
      losses += 1;
    }
  }
  const total = wins + losses + draws;
  return { wins, losses, draws, total, winRate: total ? Math.round((wins / total) * 100) : 0 };
}
